import { supabase } from '@/integrations/supabase/client';
import { AI_BEGINNERS_SEED } from '@/data/aiBeginnersSeed';
import { AI_MARKETING_SEED } from '@/data/aiMarketingSeed';

export type HubTemplate = 'course' | 'product' | 'event' | 'local' | 'marketing-course' | 'ai-for-beginners' | 'ai-marketing-course';

export interface CampaignHub {
    id: string;
    user_id: string;
    slug: string;
    title: string;
    template: HubTemplate;
    published: boolean;
    content: Record<string, unknown>;
    created_at: string;
    updated_at: string;
}

// ─── Slug generation ─────────────────────────────────────────────────────────

function toSlug(title: string, userId: string): string {
    const base = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .substring(0, 40);
    const suffix = userId.substring(0, 6);
    return `${base}-${suffix}`;
}

// ─── Template seed content ───────────────────────────────────────────────────

export function getTemplateSeed(template: HubTemplate): Record<string, unknown> | undefined {
    if (template === 'ai-for-beginners') {
        return AI_BEGINNERS_SEED as unknown as Record<string, unknown>;
    }
    if (template === 'ai-marketing-course') {
        return AI_MARKETING_SEED as unknown as Record<string, unknown>;
    }
    return undefined;
}

// ─── Create ──────────────────────────────────────────────────────────────────

export async function createCampaignHub(
    userId: string,
    title: string,
    template: HubTemplate
): Promise<{ hub: CampaignHub | null; error: string | null }> {
    const slug = toSlug(title, userId);
    const seedContent = getTemplateSeed(template);

    const { data, error } = await supabase
        .from('campaign_hubs')
        .insert({ user_id: userId, slug, title, template, ...(seedContent ? { content: seedContent } : {}) })
        .select()
        .single();

    if (error) {
        // Slug collision — append timestamp and retry
        if (error.code === '23505') {
            const slugAlt = `${slug}-${Date.now()}`;
            const { data: data2, error: error2 } = await supabase
                .from('campaign_hubs')
                .insert({ user_id: userId, slug: slugAlt, title, template, ...(seedContent ? { content: seedContent } : {}) })
                .select()
                .single();
            if (error2) return { hub: null, error: error2.message };
            return { hub: data2 as CampaignHub, error: null };
        }
        return { hub: null, error: error.message };
    }

    return { hub: data as CampaignHub, error: null };
}

// ─── Read ────────────────────────────────────────────────────────────────────

export async function getCampaignHubBySlug(
    slug: string
): Promise<CampaignHub | null> {
    const { data, error } = await supabase
        .from('campaign_hubs')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

    if (error) { console.error('getCampaignHubBySlug error:', error.message); return null; }
    return data as CampaignHub | null;
}

export async function getCampaignHubById(id: string): Promise<CampaignHub | null> {
    const { data, error } = await supabase
        .from('campaign_hubs')
        .select('*')
        .eq('id', id)
        .maybeSingle();
    if (error) { console.error('getCampaignHubById error:', error.message); return null; }
    return data as CampaignHub | null;
}

export async function getUserCampaignHubs(
    userId: string
): Promise<{ hubs: CampaignHub[]; error: string | null }> {
    const { data, error } = await supabase
        .from('campaign_hubs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

    if (error) return { hubs: [], error: error.message };
    return { hubs: (data ?? []) as CampaignHub[], error: null };
}

// ─── Save section ─────────────────────────────────────────────────────────────

export async function saveCampaignSection(
    hubId: string,
    sectionKey: string,
    value: unknown
): Promise<{ error: string | null }> {
    const { data: session } = await supabase.auth.getSession();
    const response = await fetch(`/api/campaigns/${encodeURIComponent(hubId)}/sections/${encodeURIComponent(sectionKey)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...(session.session?.access_token ? { Authorization: `Bearer ${session.session.access_token}` } : {}) },
        body: JSON.stringify({ value }),
    });
    if (response.ok) return { error: null };
    const body = await response.json().catch(() => ({}));
    return { error: body.error || `Campaign section save failed (${response.status})` };
}

// ─── Publish ──────────────────────────────────────────────────────────────────

export async function publishCampaignHub(
    hubId: string
): Promise<{ error: string | null }> {
    const { data: session } = await supabase.auth.getSession();
    const response = await fetch(`/api/campaigns/${encodeURIComponent(hubId)}/publish`, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(session.session?.access_token ? { Authorization: `Bearer ${session.session.access_token}` } : {}) }, body: JSON.stringify({ acknowledgedBindingIds: [] }) });
    if (response.ok) return { error: null };
    const body = await response.json().catch(() => ({})); return { error: body.error || `Campaign publish failed (${response.status})` };
}

export async function unpublishCampaignHub(
    hubId: string
): Promise<{ error: string | null }> {
    const { data: session } = await supabase.auth.getSession();
    const response = await fetch(`/api/campaigns/${encodeURIComponent(hubId)}/unpublish`, { method: 'POST', headers: { ...(session.session?.access_token ? { Authorization: `Bearer ${session.session.access_token}` } : {}) } });
    if (response.ok) return { error: null }; const body = await response.json().catch(() => ({})); return { error: body.error || `Campaign unpublish failed (${response.status})` };
}

// ─── Delete ──────────────────────────────────────────────────────────────────

export async function deleteCampaignHub(
    hubId: string
): Promise<{ error: string | null }> {
    const { data: session } = await supabase.auth.getSession();
    const response = await fetch(`/api/campaigns/${encodeURIComponent(hubId)}`, { method: 'DELETE', headers: { ...(session.session?.access_token ? { Authorization: `Bearer ${session.session.access_token}` } : {}) } });
    if (response.ok) return { error: null }; const body = await response.json().catch(() => ({})); return { error: body.error || `Campaign delete failed (${response.status})` };
}
